import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { get } from 'lodash';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getRepairPalDataPath, ReliabilityEntities, ReliabilityModel } from 'client/data/models/reliability';
import { getLastWordPlural } from 'client/utils/plural';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { getCompareIconClassName } from 'site-modules/shared/utils/core-page/reliability-ratings';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';
import { TooltipItem } from 'site-modules/shared/components/tooltip-item/tooltip-item';

import './reliability-ratings.scss';

const CREATIVE_ID = 'repairpal-reliability';
const TOOLTIP_VALUE_MAP = { repairpal: 'powered by RepairPal' };
const DEFAULT_HEADER = 'Reliability Ratings by RepairPal';
const DEFAULT_BODY =
  'RepairPal Reliability Ratings are based on the actual cost, frequency, and severity of unscheduled repairs and maintenance on make/model data for select 2008-2022 vehicles. The reliability of a specific vehicle may vary depending on its maintenance and driving history, model year, trim, and features.';

function ReliabilityRatingsUI({
  className,
  reliabilityRatings,
  vehicleParams: { makeName, modelName },
  bodyCopy,
  withMakeModel,
}) {
  const onTooltipOpen = useCallback(({ id }) => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_VIEW_CONTENT,
        subaction_name: TrackingConstant.VIEW_TOOLTIP,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        creative_id: CREATIVE_ID,
        value: TOOLTIP_VALUE_MAP[id] || id,
      },
    });
  }, []);

  if (!get(reliabilityRatings, 'reliabilityScore5Scale')) {
    return null;
  }

  const {
    reliabilityScore5Scale,
    repairCost,
    relativeFrequency,
    severity,
    segment,
    segmentRankByReliabilityScore,
    modelCountOfSegment,
    avgSegmentRepairCost,
    avgSegmentRelativeFrequency,
    avgSegmentSeverity,
    gearScoreDescription,
  } = reliabilityRatings;

  const sections = [
    {
      label: 'Cost',
      tooltipText: `The average total annual cost for unscheduled repairs and maintenance across all model years of the ${makeName} ${modelName} from 2008-2022.`,
      value: `${getPriceString(repairCost)}/yr`,
      compareValue: `${getPriceString(avgSegmentRepairCost)}/yr`,
      iconClassName: getCompareIconClassName(repairCost, avgSegmentRepairCost),
    },
    {
      label: 'Frequency',
      tooltipText:
        'The average number of times this model is brought into the shop for unscheduled repairs and maintenance in a single year. RepairPal calculates this metric by tracking millions of unique vehicles over multiple years to determine an average number of visits per year (omitting small routine visits, e.g., oil changes).',
      value: `${relativeFrequency}x/yr`,
      compareValue: `${avgSegmentRelativeFrequency}x/yr`,
      iconClassName: getCompareIconClassName(relativeFrequency, avgSegmentRelativeFrequency),
    },
    {
      label: 'Severity',
      tooltipText:
        'The probability that a repair will be a major issue, meaning the repair costs 3x the average annual repair cost for all models. This threshold will be higher for vehicles that have higher labor rates and parts costs (such as a premium brand).',
      value: `${severity}%`,
      compareValue: `${avgSegmentSeverity}%`,
      iconClassName: getCompareIconClassName(severity, avgSegmentSeverity),
    },
  ];

  return (
    <ScrollElement
      id="subnav-reliability"
      className={classnames('reliability-ratings rounded-12 bg-cool-gray-90 pt-1_5 pb-1 px-1', className)}
      data-tracking-parent={CREATIVE_ID}
    >
      <h2 className="heading-3 mb-1">
        {withMakeModel ? `${makeName} ${modelName} ${DEFAULT_HEADER}` : DEFAULT_HEADER}
      </h2>
      <div className="d-flex flex-column flex-md-row align-items-md-end mb-1">
        <div className="rating mb-0_25 mb-md-0 fw-bold d-flex align-items-end">
          <RatingStars rating={reliabilityScore5Scale} className="text-primary-darker size-20 me-0_5" />
          <span className="rating-number heading-3">{reliabilityScore5Scale}</span>
          <span className="medium fw-normal me-0_5">/5</span>
          {gearScoreDescription}{' '}
        </div>
        <div className="model-rank medium ps-md-0_5 ms-md-0_5">
          #{segmentRankByReliabilityScore} out of {modelCountOfSegment} among {getLastWordPlural(segment)}
        </div>
      </div>
      <div className="mb-1_5">{bodyCopy}</div>
      <Row className="mb-0_5 mb-md-1_5">
        {sections.map(({ label, tooltipText, value, compareValue, iconClassName }) => (
          <Col key={label} xs={12} md={4} className="mb-1 mb-md-0">
            <section className="rounded-12 bg-white py-1_5 px-1_5 pt-md-1">
              <div className="d-flex align-items-center mb-1_5">
                <h3 className="size-16 text-cool-gray-30 mb-0">{label}</h3>{' '}
                <TooltipItem
                  id={label}
                  className="top-0"
                  iconClassName="small"
                  item={{ text: tooltipText, placement: 'top' }}
                  onTooltipOpen={onTooltipOpen}
                />
              </div>
              <div className="text-center">
                <div className="heading-4 mb-0_5">
                  {!!iconClassName && (
                    <i className={classnames('size-16 fw-normal me-0_5', iconClassName)} aria-hidden />
                  )}
                  {value}
                </div>
                <div className="medium text-cool-gray-40">
                  vs. {compareValue}
                  <br className="hidden-md-down" /> for Average {segment}
                </div>
              </div>
            </section>
          </Col>
        ))}
      </Row>
      <div className="small fst-italic">
        powered by RepairPal{' '}
        <TooltipItem
          id="repairpal"
          className="top-0"
          iconClassName="small"
          item={{
            text:
              'Based on RepairPal reliability data as of 8/23/2023. Ratings are provided by RepairPal and Edmunds is not responsible for their accuracy.',
            placement: 'bottom',
          }}
          onTooltipOpen={onTooltipOpen}
        />
      </div>
    </ScrollElement>
  );
}

ReliabilityRatingsUI.propTypes = {
  vehicleParams: PropTypes.shape({ makeName: PropTypes.string, modelName: PropTypes.string }).isRequired,
  reliabilityRatings: ReliabilityEntities.RepairPalRatings,
  className: PropTypes.string,
  bodyCopy: PropTypes.string,
  withMakeModel: PropTypes.bool,
};

ReliabilityRatingsUI.defaultProps = {
  reliabilityRatings: null,
  className: null,
  bodyCopy: DEFAULT_BODY,
  withMakeModel: false,
};

export const ReliabilityRatings = connectToModel(ReliabilityRatingsUI, {
  reliabilityRatings: bindToPath(
    ({ vehicleParams: { makeSlug, modelSlug, year } }) => getRepairPalDataPath({ makeSlug, modelSlug, year }),
    ReliabilityModel
  ),
});
